/**
 * MainSearch Component
 *
 * Main orchestrator for adaptive search UI.
 * Selects and renders appropriate strategy based on viewport and orientation.
 *
 * SOLID Compliance:
 * - SRP: Only orchestrates strategy selection and rendering
 * - OCP: Extensible via strategy pattern (no modification needed for new strategies)
 * - LSP: All strategies are interchangeable
 * - ISP: Clean interface with minimal dependencies
 * - DIP: Depends on abstractions (URLStateManager, FilterRepository, FilterUIFactory)
 *
 * Architecture:
 * - Uses FilterUIFactory to detect viewport/orientation
 * - Creates appropriate strategy (Desktop, Mobile Portrait, Mobile Landscape)
 * - Passes URLStateManager and FilterRepository to strategy
 * - Re-evaluates strategy on window resize
 *
 * Usage:
 * <MainSearch variant="standalone" />
 * <MainSearch variant="inline" />
 *
 * Location: apps/web/src/components/search/MainSearch.jsx
 * Created: 2025-10-31
 * Part of: MVP 1.9 - Adaptive Search & Filter System
 */

import React, { useState, useEffect, useCallback } from 'react';
import useURLStateManager from '../../hooks/useURLStateManager';
import FilterRepository from '../../config/FilterRepository';
import FilterUIFactory from './strategies/FilterUIFactory';
import { useABTest } from '../../hooks/useABTest';

/**
 * MainSearch Component
 * @param {string} variant - Display variant: 'standalone' (with heading) or 'inline' (compact for header)
 */
export default function MainSearch({ variant = 'standalone' }) {
  const urlStateManager = useURLStateManager();
  const [strategy, setStrategy] = useState(null);
  const [environment, setEnvironment] = useState(null);

  // A/B Testing: Get variant for progressive disclosure experiment
  // This experiment tests different filter UI disclosure strategies
  const {
    variant: abVariant,
    isLoading: abLoading,
    trackClick: _trackClick,
    trackFilterChange
  } = useABTest('search_progressive_disclosure');

  /**
   * Initialize strategy based on current viewport/orientation
   */
  const initializeStrategy = useCallback(() => {
    const env = FilterUIFactory.getCurrentEnvironment();
    const selectedStrategy = FilterUIFactory.createStrategy(
      env.viewport,
      env.orientation,
      abVariant // Pass A/B test variant to strategy factory
    );

    setEnvironment(env);
    setStrategy(selectedStrategy);
  }, [abVariant]);

  /**
   * Handle window resize and orientation change - debounced strategy re-evaluation
   */
  useEffect(() => {
    // Initialize on mount
    initializeStrategy();

    // Debounced resize/orientation change handler
    let resizeTimeout;
    const handleEnvironmentChange = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const newEnv = FilterUIFactory.getCurrentEnvironment();

        // Only re-initialize if viewport or orientation changed
        if (
          newEnv.viewport !== environment?.viewport ||
          newEnv.orientation !== environment?.orientation
        ) {
          initializeStrategy();
        }
      }, 300); // 300ms debounce
    };

    // Listen to both resize and orientationchange events
    window.addEventListener('resize', handleEnvironmentChange);
    window.addEventListener('orientationchange', handleEnvironmentChange);

    return () => {
      window.removeEventListener('resize', handleEnvironmentChange);
      window.removeEventListener('orientationchange', handleEnvironmentChange);
      clearTimeout(resizeTimeout);
    };
  }, [initializeStrategy, environment?.orientation, environment?.viewport]);

  /**
   * Get current filters from URL
   */
  const filters = urlStateManager.getFilters();

  /**
   * Wrap URL state manager to track filter changes
   * Must delegate all methods properly (not just spread), since URLStateManager methods are on the prototype
   */
  const trackedUrlStateManager = {
    // Delegate all read methods directly
    getFilters: () => urlStateManager.getFilters(),
    getActiveFilters: () => urlStateManager.getActiveFilters(),
    getActiveFiltersForDisplay: () => urlStateManager.getActiveFiltersForDisplay(),
    hasFilters: () => urlStateManager.hasFilters(),
    buildUrl: (filters) => urlStateManager.buildUrl(filters),
    parseUrl: (searchParams) => urlStateManager.parseUrl(searchParams),
    subscribe: (callback) => urlStateManager.subscribe(callback),
    unsubscribe: (callback) => urlStateManager.unsubscribe(callback),
    getSubscriberCount: () => urlStateManager.getSubscriberCount(),

    // Wrap mutating methods with tracking
    updateFilter: (key, value) => {
      trackFilterChange({
        action: 'update',
        filterType: key,
        filterValue: value,
        timestamp: new Date().toISOString()
      });
      return urlStateManager.updateFilter(key, value);
    },
    updateFilters: (filters) => {
      trackFilterChange({
        action: 'update_multiple',
        filters: filters,
        timestamp: new Date().toISOString()
      });
      return urlStateManager.updateFilters(filters);
    },
    clearFilter: (key) => {
      trackFilterChange({
        action: 'clear',
        filterType: key,
        timestamp: new Date().toISOString()
      });
      return urlStateManager.clearFilter(key);
    },
    clearAllFilters: () => {
      trackFilterChange({
        action: 'clear_all',
        timestamp: new Date().toISOString()
      });
      return urlStateManager.clearAllFilters();
    }
  };

  /**
   * Render strategy UI
   */
  if (!strategy || abLoading) {
    // Loading state while strategy initializes or A/B test loads
    return (
      <div className="w-full p-4 text-center text-gray-500">
        Loading search...
      </div>
    );
  }

  // Get rendered UI from strategy with tracked URL state manager
  const strategyUI = strategy.render(filters, trackedUrlStateManager, FilterRepository);

  // Wrap strategy UI based on variant
  if (variant === 'inline') {
    // Inline variant (for header) - no wrapper
    return <div className="w-full">{strategyUI}</div>;
  }

  // Standalone variant (for main page) - with heading and centering
  return (
    <div className="w-full">
      {/* Desktop Heading */}
      <div className="hidden md:flex mb-6 sm:mb-8 flex-col items-center">
        <div className="w-[80%] max-w-6xl">
          <h2 className="text-lg sm:text-xl font-medium text-gray-700 mb-3 sm:mb-4">
            What is your next adventure?
          </h2>
          {strategyUI}
        </div>
      </div>

      {/* Mobile - No heading, just strategy UI */}
      <div className="md:hidden px-4 mb-6">
        {strategyUI}
      </div>
    </div>
  );
}
